import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { DEFAULT_SETTINGS, getPopupResetDefaults, MAX_WIDTH_MAX, MAX_WIDTH_MIN, MAX_WIDTH_STEP, syncPracticeFlags, type Settings } from '@core/settings/schema.ts';
import {
  copyTrackStyleBetweenSlots,
  getTrackStyleResetDefaults,
  type BoxShadowPreset,
  type ShadowPreset,
} from '@core/styles/stylePresets.ts';
import {
  buildStyleLanguageBindingHints,
  buildStyleStudioLanguageOptions,
} from '@core/styles/styleStudioLanguageCatalog.ts';
import type { SettingsStore } from '@ports/SettingsStore.ts';
import type { SubtitleStore, SubtitleSlot } from '../state/subtitleStore.ts';
import { buildSrtFilename, downloadTextFile, encodeSrt } from '../lib/encodeSrt.ts';
import { clearSlotTranslations } from '../lib/bilingualDefaults.ts';
import { buildDocumentationUrl } from '../lib/docsUrl.ts';
import { useI18n } from '../i18n/index.ts';
import type { StringKey, UiLocale } from '../i18n/strings.ts';
import { SheetHandle } from './SheetHandle.tsx';
import {
  learningTargetLanguageEntries,
  migrateLearningTargetLanguage,
} from '../lib/azureLanguage.ts';
import azureJson from '../../../extension/src/adapters/resource/json/azure.json';

const STYLE_LANGUAGE_OPTIONS = buildStyleStudioLanguageOptions(
  azureJson.translation as Record<string, { name: string; nativeName: string }>,
);

const FONT_PRESETS = [
  { id: 'netflix', labelKey: 'settingsFontPresetNetflix' as StringKey, value: 'Netflix Sans,Helvetica Neue,Segoe UI,Roboto,Ubuntu,sans-serif' },
  { id: 'arial', labelKey: 'settingsFontPresetArial' as StringKey, value: 'Arial, sans-serif' },
  { id: 'verdana', labelKey: 'settingsFontPresetVerdana' as StringKey, value: 'Verdana, sans-serif' },
  { id: 'tahoma', labelKey: 'settingsFontPresetTahoma' as StringKey, value: 'Tahoma, sans-serif' },
  { id: 'trebuchet', labelKey: 'settingsFontPresetTrebuchet' as StringKey, value: 'Trebuchet MS, sans-serif' },
  { id: 'times', labelKey: 'settingsFontPresetTimes' as StringKey, value: 'Times New Roman, serif' },
  { id: 'georgia', labelKey: 'settingsFontPresetGeorgia' as StringKey, value: 'Georgia, serif' },
  { id: 'garamond', labelKey: 'settingsFontPresetGaramond' as StringKey, value: 'Garamond, serif' },
  { id: 'courier', labelKey: 'settingsFontPresetCourier' as StringKey, value: 'Courier New, monospace' },
  { id: 'brush', labelKey: 'settingsFontPresetBrush' as StringKey, value: 'Brush Script MT, cursive' },
] as const;

const SHADOW_OPTIONS: { value: ShadowPreset; labelKey: StringKey }[] = [
  { value: '', labelKey: 'settingsShadowNone' },
  { value: 'raised', labelKey: 'settingsShadowRaised' },
  { value: 'depressed', labelKey: 'settingsShadowDepressed' },
  { value: 'outline', labelKey: 'settingsShadowOutline' },
  { value: 'shadow', labelKey: 'settingsShadowShadow' },
];

const BOX_SHADOW_OPTIONS: { value: BoxShadowPreset; labelKey: StringKey }[] = [
  { value: '', labelKey: 'settingsBoxShadowNone' },
  { value: 'soft', labelKey: 'settingsBoxShadowSoft' },
  { value: 'medium', labelKey: 'settingsBoxShadowMedium' },
];

export type SettingsPanelProps = {
  settingsStore: SettingsStore;
  store: SubtitleStore;
  open: boolean;
  onClose: () => void;
  /** Membership unlock — engines stay selectable; translate-time gate uses this (extension parity). */
  unlock?: boolean;
  /** Import a local subtitle file into a slot (extension Import section). */
  onImportFile?: (slot: SubtitleSlot, file: File) => void | Promise<void>;
  readerEnabled?: boolean;
  onReaderEnabledChange?: (enabled: boolean) => void;
  onOpenCards?: () => void;
  onOpenStyleStudio?: () => void;
  /** Used for SRT export filenames (defaults to "video"). */
  videoId?: string;
};

function RangeRow({
  label,
  value,
  min,
  max,
  step,
  onChange,
  testId,
  split = false,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  testId?: string;
  /** Extension popup layout: label left, control right. */
  split?: boolean;
}) {
  const [adjusting, setAdjusting] = useState(false);
  const valueText = Number.isInteger(step) ? String(value) : value.toFixed(2);
  return (
    <label
      className={`settings-panel__row${split ? ' settings-panel__row--split' : ''}${adjusting ? ' range-adjusting' : ''}`}
    >
      <span>
        {label}
        {!split ? (
          <span className="settings-panel__range-value">{` (${valueText})`}</span>
        ) : null}
      </span>
      <input
        data-testid={testId}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        onPointerDown={() => setAdjusting(true)}
        onPointerUp={() => setAdjusting(false)}
        onPointerCancel={() => setAdjusting(false)}
        onBlur={() => setAdjusting(false)}
        onInput={() => setAdjusting(true)}
      />
    </label>
  );
}

function StyleControls({
  slot,
  settings,
  patch,
  t,
}: {
  slot: 'main' | 'second';
  settings: Settings;
  patch: (partial: Partial<Settings>) => void;
  t: (key: StringKey) => string;
}) {
  const prefix = slot === 'main' ? 'main' : 'second';
  const fontSize = settings[`${prefix}FontSizeRatio`];
  const fontColor = settings[`${prefix}FontColor`];
  const fontWeight = settings[`${prefix}FontWeight`];
  const strokeWidth = settings[`${prefix}StrokeWidth`];
  const strokeColor = settings[`${prefix}StrokeColor`];
  const shadow = settings[`${prefix}Shadow`] as ShadowPreset;
  const shadowColor = settings[`${prefix}ShadowColor`];
  const bgColor = settings[`${prefix}FontBackgroundColor`];
  const bgOpacity = settings[`${prefix}FontBackgroundColorOpacity`];
  const fontFamily = settings[`${prefix}FontFamily`];
  const letterSpacing = settings[`${prefix}LetterSpacing`];
  const lineHeight = settings[`${prefix}LineHeight`];
  const padding = settings[`${prefix}Padding`];
  const opacity = settings[`${prefix}Opacity`];
  const boxShadow = settings[`${prefix}BoxShadow`] as BoxShadowPreset;
  const googleFamilies = settings[`${prefix}FontGoogleFamilies`];
  const googleCss = settings[`${prefix}FontGoogleCssProperties`];
  const familyValue = FONT_PRESETS.some((p) => p.value === fontFamily)
    ? fontFamily
    : FONT_PRESETS[0]!.value;

  return (
    <>
      <RangeRow
        label={t('settingsFontSize')}
        testId={`settings-${slot}-font-size`}
        value={fontSize}
        min={0.5}
        max={1.5}
        step={0.1}
        onChange={(v) => patch({ [`${prefix}FontSizeRatio`]: v } as Partial<Settings>)}
      />
      <RangeRow
        label={t('settingsFontWeight')}
        value={fontWeight}
        min={100}
        max={900}
        step={100}
        onChange={(v) => patch({ [`${prefix}FontWeight`]: v } as Partial<Settings>)}
      />
      <label className="settings-panel__row">
        <span>{t('settingsFontColor')}</span>
        <input
          data-testid={`settings-${slot}-color`}
          type="color"
          value={fontColor}
          onChange={(e) => patch({ [`${prefix}FontColor`]: e.target.value } as Partial<Settings>)}
        />
      </label>
      <RangeRow
        label={t('settingsStroke')}
        value={strokeWidth}
        min={0}
        max={10}
        step={0.5}
        onChange={(v) => patch({ [`${prefix}StrokeWidth`]: v } as Partial<Settings>)}
      />
      <label className="settings-panel__row">
        <span>{t('settingsStrokeColor')}</span>
        <input
          data-testid={`settings-${slot}-stroke-color`}
          type="color"
          value={strokeColor}
          onChange={(e) => patch({ [`${prefix}StrokeColor`]: e.target.value } as Partial<Settings>)}
        />
      </label>
      <label className="settings-panel__row">
        <span>{t('settingsShadow')}</span>
        <select
          data-testid={`settings-${slot}-shadow`}
          value={shadow}
          onChange={(e) => patch({ [`${prefix}Shadow`]: e.target.value } as Partial<Settings>)}
        >
          {SHADOW_OPTIONS.map((opt) => (
            <option key={opt.value || 'none'} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </label>
      <label className="settings-panel__row">
        <span>{t('settingsShadowColor')}</span>
        <input
          type="color"
          value={shadowColor}
          onChange={(e) => patch({ [`${prefix}ShadowColor`]: e.target.value } as Partial<Settings>)}
        />
      </label>
      <label className="settings-panel__row">
        <span>{t('settingsBackground')}</span>
        <input
          type="color"
          value={bgColor}
          onChange={(e) => patch({ [`${prefix}FontBackgroundColor`]: e.target.value } as Partial<Settings>)}
        />
      </label>
      <RangeRow
        label={t('settingsBackgroundOpacity')}
        value={bgOpacity}
        min={0}
        max={1}
        step={0.1}
        onChange={(v) => patch({ [`${prefix}FontBackgroundColorOpacity`]: v } as Partial<Settings>)}
      />
      <RangeRow
        label={t('settingsLetterSpacing')}
        value={letterSpacing}
        min={-0.1}
        max={0.2}
        step={0.01}
        onChange={(v) => patch({ [`${prefix}LetterSpacing`]: v } as Partial<Settings>)}
      />
      <RangeRow
        label={t('settingsLineHeight')}
        value={lineHeight}
        min={1}
        max={2}
        step={0.05}
        onChange={(v) => patch({ [`${prefix}LineHeight`]: v } as Partial<Settings>)}
      />
      <RangeRow
        label={t('settingsPadding')}
        value={padding}
        min={0}
        max={24}
        step={1}
        onChange={(v) => patch({ [`${prefix}Padding`]: v } as Partial<Settings>)}
      />
      <RangeRow
        label={t('settingsOpacity')}
        value={opacity}
        min={0.2}
        max={1}
        step={0.05}
        onChange={(v) => patch({ [`${prefix}Opacity`]: v } as Partial<Settings>)}
      />
      <label className="settings-panel__row">
        <span>{t('settingsBoxShadow')}</span>
        <select
          data-testid={`settings-${slot}-box-shadow`}
          value={boxShadow}
          onChange={(e) => patch({ [`${prefix}BoxShadow`]: e.target.value } as Partial<Settings>)}
        >
          {BOX_SHADOW_OPTIONS.map((opt) => (
            <option key={opt.value || 'none'} value={opt.value}>
              {t(opt.labelKey)}
            </option>
          ))}
        </select>
      </label>
      <label className="settings-panel__row">
        <span>{t('settingsFontFamily')}</span>
        <select
          data-testid={`settings-${slot}-font-family`}
          value={familyValue}
          onChange={(e) => patch({ [`${prefix}FontFamily`]: e.target.value } as Partial<Settings>)}
        >
          {FONT_PRESETS.map((p) => (
            <option key={p.id} value={p.value}>
              {t(p.labelKey)}
            </option>
          ))}
        </select>
      </label>
      <label className="settings-panel__row">
        <span>{t('settingsFontGoogleFamilies')}</span>
        <input
          data-testid={`settings-${slot}-google-families`}
          type="text"
          value={googleFamilies}
          onChange={(e) => patch({ [`${prefix}FontGoogleFamilies`]: e.target.value } as Partial<Settings>)}
          onBlur={(e) => patch({ [`${prefix}FontGoogleFamilies`]: e.target.value.trim() } as Partial<Settings>)}
        />
      </label>
      <label className="settings-panel__row">
        <span>{t('settingsFontGoogleCss')}</span>
        <textarea
          data-testid={`settings-${slot}-google-css`}
          rows={2}
          value={googleCss}
          onChange={(e) => patch({ [`${prefix}FontGoogleCssProperties`]: e.target.value } as Partial<Settings>)}
          onBlur={(e) => patch({ [`${prefix}FontGoogleCssProperties`]: e.target.value.trim() } as Partial<Settings>)}
        />
      </label>
    </>
  );
}

export function SettingsPanel({
  settingsStore,
  store,
  open,
  onClose,
  unlock = false,
  onImportFile,
  readerEnabled = false,
  onReaderEnabledChange,
  onOpenCards,
  onOpenStyleStudio,
  videoId = 'video',
}: SettingsPanelProps) {
  const { t, locale, setLocale } = useI18n();
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [exportToast, setExportToast] = useState<string | null>(null);
  const settings = useSyncExternalStore(
    (cb) => settingsStore.subscribe(cb),
    () => settingsStore.getSnapshot?.() ?? DEFAULT_SETTINGS,
  );
  const mainCueCount = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('main').cues?.length ?? 0,
  );
  const secondCueCount = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('second').cues?.length ?? 0,
  );
  const mainSelected = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('main').selectedId,
  );
  const secondSelected = useSyncExternalStore(
    (cb) => store.subscribe(cb),
    () => store.getSlot('second').selectedId,
  );
  const canExportMain = Boolean(mainSelected) && mainCueCount > 0;
  const canExportSecond = Boolean(secondSelected) && secondCueCount > 0;

  const styleLanguageHints = useMemo(
    () => buildStyleLanguageBindingHints(
      settings.styleLanguagePriorities,
      settings.customStylePresets,
      STYLE_LANGUAGE_OPTIONS,
    ),
    [settings.styleLanguagePriorities, settings.customStylePresets],
  );
  const docsHref = buildDocumentationUrl(locale);

  useEffect(() => {
    if (!open) return;
    const load = () => setVoices(window.speechSynthesis?.getVoices() ?? []);
    load();
    window.speechSynthesis?.addEventListener('voiceschanged', load);
    return () => window.speechSynthesis?.removeEventListener('voiceschanged', load);
  }, [open]);

  if (!open) return null;

  function patch(partial: Parameters<SettingsStore['set']>[0]) {
    void settingsStore.set(partial);
  }

  function exportSlot(slot: SubtitleSlot) {
    const state = store.getSlot(slot);
    const cues = state.cues;
    // Extension: export enabled when a track is selected; we also require cues.
    if (!state.selectedId || !cues?.length) return;
    const track = state.trackList?.subtitles.find(
      (entry) => String(entry.value) === state.selectedId,
    );
    downloadTextFile(buildSrtFilename(videoId, slot, track?.code), encodeSrt(cues));
    setExportToast(t('exportSrtDone'));
    window.setTimeout(() => setExportToast(null), 2000);
  }

  const engine = settings.translationEngine || 'google';
  const memberSuffix = !unlock ? t('settingsMembersSuffix') : '';

  return (
    <aside className="settings-panel panel-sheet" data-testid="settings-panel" aria-label={t('settings')}>
      <SheetHandle onDismiss={onClose} />
      <div className="settings-panel__header">
        <h2>{t('settings')}</h2>
        <button
          type="button"
          className="settings-panel__close"
          onClick={onClose}
          aria-label={t('settingsClose')}
        >
          {t('settingsClose')}
        </button>
      </div>

      <div className="settings-panel__body" data-testid="settings-body">

          {/* Extension popup settings order: position → spacing → maxWidth → engine → toggles → … */}
          <section className="settings-panel__section">
            <h3>{t('settingsPosition')}</h3>
            {!settings.subtitleSeparation ? (
              <RangeRow
                label={t('settingsSubY')}
                testId="settings-subY"
                value={settings.subY}
                min={0}
                max={1}
                step={0.01}
                onChange={(subY) => patch({ subY })}
              />
            ) : (
              <>
                <RangeRow
                  label={t('settingsMainSubY')}
                  testId="settings-mainSubY"
                  value={settings.mainSubY}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(mainSubY) => patch({ mainSubY })}
                />
                <RangeRow
                  label={t('settingsSecondSubY')}
                  testId="settings-secondSubY"
                  value={settings.secondSubY}
                  min={0}
                  max={1}
                  step={0.01}
                  onChange={(secondSubY) => patch({ secondSubY })}
                />
              </>
            )}
            {!settings.subtitleSeparation ? (
              <RangeRow
                label={t('settingsSpacing')}
                testId="settings-spacing"
                value={settings.spacing}
                min={0}
                max={0.015}
                step={0.001}
                onChange={(spacing) => patch({ spacing })}
              />
            ) : null}
            <RangeRow
              label={t('settingsMaxWidth')}
              testId="settings-max-width"
              value={settings.maxWidth}
              min={MAX_WIDTH_MIN}
              max={MAX_WIDTH_MAX}
              step={MAX_WIDTH_STEP}
              onChange={(maxWidth) => patch({ maxWidth })}
            />
          </section>

          <section className="settings-panel__section">
            <label className="settings-panel__row settings-panel__row--split">
              <span>{t('settingsEngine')}</span>
              <select
                className="field"
                data-testid="translation-engine"
                value={engine}
                onChange={(e) => {
                  const next = e.target.value;
                  // Extension popup: all engines selectable; membership is gated at translate time.
                  if (next !== engine) {
                    clearSlotTranslations(store, 'main');
                    clearSlotTranslations(store, 'second');
                  }
                  patch({ translationEngine: next });
                }}
              >
                <option value="google">Google</option>
                <option value="azure">Azure{memberSuffix}</option>
                <option value="deepl">DeepL{memberSuffix}</option>
                <option value="ai">AI{memberSuffix}</option>
                <option value="batch">AI (Batch){memberSuffix}</option>
              </select>
            </label>
          </section>

          <section className="settings-panel__section">
            <label className="settings-panel__check">
              <input
                data-testid="settings-selectable"
                type="checkbox"
                checked={settings.selectable}
                onChange={(e) => patch({ selectable: e.target.checked })}
              />
              {t('settingsSelectable')}
            </label>
            {/* Subtitle-list toggle temporarily hidden; cue panel still opens from player UI. */}
            <label className="settings-panel__check">
              <input
                data-testid="settings-dark-mode"
                type="checkbox"
                checked={settings.darkMode}
                onChange={(e) => patch({ darkMode: e.target.checked })}
              />
              {t('settingsDarkMode')}
            </label>
          </section>

          <section className="settings-panel__section" data-testid="settings-practice">
            <h3>{t('settingsPracticeMode')}</h3>
            <div
              className="segmented settings-panel__practice-modes"
              role="group"
              aria-label={t('settingsPracticeMode')}
              data-testid="settings-practice-mode"
            >
              {(
                [
                  ['watch', t('settingsPracticeWatch')],
                  ['dictation', t('settingsPracticeDictation')],
                  ['speaking', t('settingsPracticeSpeaking')],
                  ['shadowing', t('settingsPracticeShadowing')],
                  ['learning', t('settingsPracticeLearning')],
                ] as const
              ).map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  className={`segmented__btn${settings.practiceMode === id ? ' segmented__btn--active' : ''}`}
                  data-testid={`settings-practice-mode-${id}`}
                  aria-pressed={settings.practiceMode === id}
                  onClick={() => patch(syncPracticeFlags(id))}
                >
                  {label}
                </button>
              ))}
            </div>

            {settings.practiceMode === 'shadowing' ? (
              <div
                className="settings-panel__practice-panel"
                data-testid="settings-practice-panel-shadowing"
              >
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsShadowingGrayPast')}</span>
                  <input
                    data-testid="settings-shadowing-gray-past"
                    type="checkbox"
                    checked={settings.shadowingGrayPast !== false}
                    onChange={(e) => patch({ shadowingGrayPast: e.target.checked })}
                  />
                </label>
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsShadowingHideUpcoming')}</span>
                  <input
                    data-testid="settings-shadowing-hide-upcoming"
                    type="checkbox"
                    checked={Boolean(settings.shadowingHideUpcoming)}
                    onChange={(e) => patch({ shadowingHideUpcoming: e.target.checked })}
                  />
                </label>
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsShadowingAutoRemoveLineBreaks')}</span>
                  <input
                    data-testid="settings-shadowing-auto-remove-line-breaks"
                    type="checkbox"
                    checked={settings.shadowingAutoRemoveLineBreaks !== false}
                    onChange={(e) => patch({ shadowingAutoRemoveLineBreaks: e.target.checked })}
                  />
                </label>
              </div>
            ) : null}

            {settings.practiceMode === 'dictation' ? (
              <div
                className="settings-panel__practice-panel"
                data-testid="settings-practice-panel-dictation"
              >
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsDictationSubtitle')}</span>
                  <select
                    data-testid="settings-dictation-subtitle"
                    value={
                      settings.dictationSubtitle === 'second' ||
                      settings.dictationSubtitle === 'secondary'
                        ? 'secondary'
                        : 'main'
                    }
                    onChange={(e) => patch({ dictationSubtitle: e.target.value })}
                  >
                    <option value="main">{t('settingsDictationMain')}</option>
                    <option value="secondary">{t('settingsDictationSecond')}</option>
                  </select>
                </label>
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsDictationFiltered')}</span>
                  <textarea
                    data-testid="settings-dictation-filtered"
                    rows={3}
                    value={settings.dictationFilteredWords}
                    onChange={(e) => patch({ dictationFilteredWords: e.target.value })}
                    onBlur={(e) => patch({ dictationFilteredWords: e.target.value.trim() })}
                  />
                </label>
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsDictationIgnoreBrackets')}</span>
                  <input
                    data-testid="settings-dictation-brackets"
                    type="checkbox"
                    checked={settings.dictationIgnoreBracketedText}
                    onChange={(e) => patch({ dictationIgnoreBracketedText: e.target.checked })}
                  />
                </label>
                <RangeRow
                  label={t('settingsDictationTypingVolume')}
                  value={settings.dictationTypingSoundVolume}
                  min={0}
                  max={1}
                  step={0.1}
                  split
                  onChange={(v) => patch({ dictationTypingSoundVolume: v })}
                  testId="settings-dictation-typing-volume"
                />
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsDictationInputPosition')}</span>
                  <select
                    data-testid="settings-dictation-input-position"
                    value={settings.dictationInputPosition === 'top' ? 'top' : 'bottom'}
                    onChange={(e) => patch({ dictationInputPosition: e.target.value })}
                  >
                    <option value="top">{t('settingsDictationPositionTop')}</option>
                    <option value="bottom">{t('settingsDictationPositionBottom')}</option>
                  </select>
                </label>
              </div>
            ) : null}

            {settings.practiceMode === 'speaking' ? (
              <div
                className="settings-panel__practice-panel"
                data-testid="settings-practice-panel-speaking"
              >
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsDictationSubtitle')}</span>
                  <select
                    data-testid="settings-dictation-subtitle"
                    value={
                      settings.dictationSubtitle === 'second' ||
                      settings.dictationSubtitle === 'secondary'
                        ? 'secondary'
                        : 'main'
                    }
                    onChange={(e) => patch({ dictationSubtitle: e.target.value })}
                  >
                    <option value="main">{t('settingsDictationMain')}</option>
                    <option value="secondary">{t('settingsDictationSecond')}</option>
                  </select>
                </label>
                <RangeRow
                  label={t('settingsSpeakingVolume')}
                  value={settings.speakingPlaybackVolume}
                  min={0}
                  max={1}
                  step={0.1}
                  split
                  onChange={(v) => patch({ speakingPlaybackVolume: v })}
                  testId="settings-speaking-volume"
                />
              </div>
            ) : null}

            {settings.practiceMode === 'learning' ? (
              <div
                className="settings-panel__practice-panel"
                data-testid="settings-practice-panel-learning"
              >
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsDictationSubtitle')}</span>
                  <select
                    data-testid="settings-dictation-subtitle"
                    value={
                      settings.dictationSubtitle === 'second' ||
                      settings.dictationSubtitle === 'secondary'
                        ? 'secondary'
                        : 'main'
                    }
                    onChange={(e) => patch({ dictationSubtitle: e.target.value })}
                  >
                    <option value="main">{t('settingsDictationMain')}</option>
                    <option value="secondary">{t('settingsDictationSecond')}</option>
                  </select>
                </label>
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsLearningTarget')}</span>
                  <select
                    data-testid="settings-learning-target"
                    value={migrateLearningTargetLanguage(settings.learningModeTargetLanguage) || ''}
                    onChange={(e) =>
                      patch({
                        learningModeTargetLanguage: migrateLearningTargetLanguage(e.target.value),
                      })
                    }
                  >
                    <option value="">{t('learnTargetNone')}</option>
                    {learningTargetLanguageEntries().map(({ code, label }) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="settings-panel__row settings-panel__row--split">
                  <span>{t('settingsLearningDefaultPrompt')}</span>
                  <select
                    data-testid="settings-learning-default-prompt"
                    value={settings.learningModeDefaultPromptId || '2'}
                    onChange={(e) => patch({ learningModeDefaultPromptId: e.target.value })}
                  >
                    <option value="0">{t('learnPromptMeaning')}</option>
                    <option value="1">{t('learnPromptExamples')}</option>
                    <option value="2">{t('learnPromptDictionary')}</option>
                  </select>
                </label>
                {onOpenCards ? (
                  <div className="settings-panel__row settings-panel__row--split">
                    <span>{t('cardsTitle')}</span>
                    <button
                      type="button"
                      className="settings-panel__link-btn"
                      data-testid="settings-open-cards"
                      onClick={onOpenCards}
                    >
                      {t('settingsLearningCardsLink')}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : null}
          </section>

          <section className="settings-panel__section">
            <h3>{t('settingsLanguage')}</h3>
            <select
              data-testid="ui-locale"
              value={locale}
              onChange={(e) => setLocale(e.target.value as UiLocale)}
            >
              <option value="en">English</option>
              <option value="zh-TW">繁體中文</option>
              <option value="zh-CN">简体中文</option>
              <option value="ja">日本語</option>
              <option value="ko">한국어</option>
            </select>
          </section>

          <section
            className="settings-panel__section"
            data-testid="settings-subtitle-optimize-section"
          >
            <h3>{t('settingsSubtitleOptimize')}</h3>
            <label className="settings-panel__check">
              <input
                data-testid="settings-subtitle-optimize"
                type="checkbox"
                checked={settings.subtitleOptimize}
                onChange={(e) => patch({ subtitleOptimize: e.target.checked })}
              />
              {t('settingsSubtitleOptimizeEnable')}
            </label>
            <label className="settings-panel__row settings-panel__row--split">
              <span>{t('settingsSubtitleOptimizeModel')}</span>
              <select
                className="field"
                data-testid="settings-subtitle-optimize-model"
                value={settings.subtitleOptimizeModel}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'auto' || value === 'small' || value === 'large') {
                    patch({ subtitleOptimizeModel: value });
                  }
                }}
              >
                <option value="auto">{t('settingsSubtitleOptimizeModelAuto')}</option>
                <option value="small">{t('settingsSubtitleOptimizeModelSmall')}</option>
                <option value="large">{t('settingsSubtitleOptimizeModelLarge')}</option>
              </select>
            </label>
          </section>

          <section className="settings-panel__section" data-testid="settings-style-studio">
            <h3>{t('styleStudioOpen')}</h3>
            {onOpenStyleStudio ? (
              <button
                type="button"
                className="btn"
                data-testid="settings-open-style-studio"
                onClick={onOpenStyleStudio}
              >
                {t('styleStudioOpen')}
              </button>
            ) : (
              <p className="settings-panel__hint">{t('styleStudioOpen')}</p>
            )}
            {styleLanguageHints.length > 0 ? (
              <div className="settings-panel__style-hints" data-testid="settings-style-lang-hints">
                <p className="settings-panel__hint">{t('styleStudioLangBind')}</p>
                <p className="settings-panel__hint">{t('styleStudioLangBindHint')}</p>
                <ul className="settings-panel__style-hints-list">
                  {styleLanguageHints.map((hint) => (
                    <li key={hint.styleName}>
                      <span className="settings-panel__style-hints-name">{hint.styleName}</span>
                      {` — ${hint.languageLabels.join(', ')}`}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <section className="settings-panel__section" data-testid="settings-style-main">
            <h3>{t('settingsStyleMain')}</h3>
            <StyleControls slot="main" settings={settings} patch={patch} t={t} />
            <div className="settings-panel__row-actions">
              <button
                type="button"
                className="btn"
                data-testid="style-copy-from-second"
                onClick={() => patch(copyTrackStyleBetweenSlots(settings, 'second', 'main'))}
              >
                {t('settingsStyleCopyFromSecond')}
              </button>
              <button
                type="button"
                className="btn settings-panel__reset-track"
                data-testid="style-reset-main"
                onClick={() => patch(getTrackStyleResetDefaults('main'))}
              >
                {t('settingsStyleResetTrack')}
              </button>
            </div>
          </section>

          <section className="settings-panel__section" data-testid="settings-style-second">
            <h3>{t('settingsStyleSecond')}</h3>
            <StyleControls slot="second" settings={settings} patch={patch} t={t} />
            <div className="settings-panel__row-actions">
              <button
                type="button"
                className="btn"
                data-testid="style-copy-from-main"
                onClick={() => patch(copyTrackStyleBetweenSlots(settings, 'main', 'second'))}
              >
                {t('settingsStyleCopyFromMain')}
              </button>
              <button
                type="button"
                className="btn settings-panel__reset-track"
                data-testid="style-reset-second"
                onClick={() => patch(getTrackStyleResetDefaults('second'))}
              >
                {t('settingsStyleResetTrack')}
              </button>
            </div>
          </section>

          <section className="settings-panel__section">
            <h3>{t('settingsFurigana')}</h3>
            <label className="settings-panel__check">
              <input
                data-testid="settings-furigana-enable"
                type="checkbox"
                checked={settings.furiganaEnable}
                onChange={(e) => patch({ furiganaEnable: e.target.checked })}
              />
              {t('settingsFuriganaEnable')}
            </label>
            <label className="settings-panel__row">
              <span>{t('settingsFuriganaConvert')}</span>
              <select
                data-testid="settings-furigana-convert"
                value={settings.furiganaConvertTo}
                onChange={(e) => patch({ furiganaConvertTo: e.target.value })}
              >
                <option value="hiragana">Hiragana</option>
                <option value="katakana">Katakana</option>
                <option value="romaji">Romaji</option>
              </select>
            </label>
            <label className="settings-panel__row">
              <span>{t('settingsFuriganaRomaji')}</span>
              <select
                data-testid="settings-furigana-romaji"
                value={settings.furiganaRomaji}
                onChange={(e) => patch({ furiganaRomaji: e.target.value })}
              >
                <option value="nippon">Nippon</option>
                <option value="passport">Passport</option>
                <option value="hepburn">Hepburn</option>
              </select>
            </label>
          </section>

          <section className="settings-panel__section">
            <h3>{t('settingsReader')}</h3>
            <label className="settings-panel__check">
              <input
                data-testid="settings-reader-enable"
                type="checkbox"
                checked={readerEnabled}
                onChange={(e) => onReaderEnabledChange?.(e.target.checked)}
              />
              {t('settingsReaderEnable')}
            </label>
            <label className="settings-panel__row">
              <span>{t('settingsReaderVoice')}</span>
              <select
                data-testid="settings-reader-voice"
                value={settings.subtitleReaderVoice}
                onChange={(e) => patch({ subtitleReaderVoice: e.target.value })}
              >
                <option value="">{t('settingsReaderVoiceDefault')}</option>
                {voices.map((voice) => (
                  <option key={`${voice.name}-${voice.lang}`} value={voice.name}>
                    {voice.name} ({voice.lang})
                  </option>
                ))}
              </select>
            </label>
            <RangeRow
              label={t('settingsReaderRate')}
              testId="settings-reader-rate"
              value={settings.subtitleReaderRate}
              min={1}
              max={10}
              step={1}
              onChange={(subtitleReaderRate) => patch({ subtitleReaderRate })}
            />
            <RangeRow
              label={t('settingsReaderVolume')}
              testId="settings-reader-volume"
              value={settings.subtitleReaderVolume}
              min={0.1}
              max={1}
              step={0.1}
              onChange={(subtitleReaderVolume) => patch({ subtitleReaderVolume })}
            />
          </section>

          <section className="settings-panel__section">
            <h3>{t('settingsPreprocess')}</h3>
            <label className="settings-panel__check">
              <input
                data-testid="settings-preprocess-1"
                type="checkbox"
                checked={settings.preProcessing1}
                onChange={(e) => patch({ preProcessing1: e.target.checked })}
              />
              {t('settingsPreprocess1')}
            </label>
            <label className="settings-panel__check">
              <input
                data-testid="settings-preprocess-2"
                type="checkbox"
                checked={settings.preProcessing2}
                onChange={(e) => patch({ preProcessing2: e.target.checked })}
              />
              {t('settingsPreprocess2')}
            </label>
            <label className="settings-panel__check">
              <input
                data-testid="settings-preprocess-3"
                type="checkbox"
                checked={settings.preProcessing3}
                onChange={(e) => patch({ preProcessing3: e.target.checked })}
              />
              {t('settingsPreprocess3')}
            </label>
          </section>

          <section className="settings-panel__section" data-testid="settings-import-srt">
            <h3>{t('importSrt')}</h3>
            <label className="settings-panel__row settings-panel__row--split">
              <span>{t('importSrtMain')}</span>
              <label className="btn settings-panel__file-btn">
                {t('importSrtUpload')}
                <input
                  data-testid="settings-import-main"
                  type="file"
                  accept=".vtt,.srt,.json,.json3,text/vtt,application/json"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void onImportFile?.('main', file);
                  }}
                />
              </label>
            </label>
            <label className="settings-panel__row settings-panel__row--split">
              <span>{t('importSrtSecond')}</span>
              <label className="btn settings-panel__file-btn">
                {t('importSrtUpload')}
                <input
                  data-testid="settings-import-second"
                  type="file"
                  accept=".vtt,.srt,.json,.json3,text/vtt,application/json"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = '';
                    if (file) void onImportFile?.('second', file);
                  }}
                />
              </label>
            </label>
          </section>

          <section className="settings-panel__section" data-testid="settings-export-srt">
            <h3>{t('exportSrt')}</h3>
            <label className="settings-panel__row settings-panel__row--split">
              <span>{t('exportSrtMain')}</span>
              <button
                type="button"
                className="btn"
                data-testid="settings-export-main"
                disabled={!canExportMain}
                title={!canExportMain ? t('exportSrtNeedTrack') : undefined}
                onClick={() => exportSlot('main')}
              >
                {t('exportSrtDone')}
              </button>
            </label>
            <label className="settings-panel__row settings-panel__row--split">
              <span>{t('exportSrtSecond')}</span>
              <button
                type="button"
                className="btn"
                data-testid="settings-export-second"
                disabled={!canExportSecond}
                title={!canExportSecond ? t('exportSrtNeedTrack') : undefined}
                onClick={() => exportSlot('second')}
              >
                {t('exportSrtDone')}
              </button>
            </label>
            {exportToast ? (
              <p className="settings-panel__hint" role="status" data-testid="settings-export-toast">
                {exportToast}
              </p>
            ) : null}
          </section>

          <section className="settings-panel__section" data-testid="settings-other-section">
            <h3>{t('settingsTabAdvanced')}</h3>
            <label className="settings-panel__check">
              <input
                data-testid="settings-separation"
                type="checkbox"
                checked={settings.subtitleSeparation}
                onChange={(e) => patch({ subtitleSeparation: e.target.checked })}
              />
              {t('settingsSeparation')}
            </label>
            {/* Hover-to-reveal UI temporarily hidden; runtime also gated in SubtitleOverlay. */}
          </section>

          <section className="settings-panel__section">
            <h3>{t('settingsDocs')}</h3>
            <a
              className="settings-panel__docs-link"
              data-testid="settings-docs-link"
              href={docsHref}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('settingsDocsOpen')}
            </a>
          </section>

          <section className="settings-panel__section" data-testid="settings-reset-all-section">
            <button
              type="button"
              className="btn settings-panel__reset-all"
              data-testid="settings-reset-all"
              onClick={() => {
                // Extension popup reset: apply getPopupResetDefaults with no confirm.
                patch(getPopupResetDefaults());
              }}
            >
              {t('settingsResetAll')}
            </button>
          </section>
      </div>
    </aside>
  );
}
